/**
 * Session Lifecycle Hook
 * Manages proactive session lifecycle: heartbeat keepalive,
 * expiry clock, and auth config loading.
 * @packageDocumentation
 */

import { useEffect } from 'react';

import { useAuthStore } from '@/stores/auth.js';

/** Interval at which the heartbeat keepalive fires. */
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000;

/** Interval at which the client-side expiry clock is evaluated. */
const EXPIRY_CHECK_INTERVAL_MS = 30 * 1000;

/**
 * Calculates how many milliseconds remain until the session expires.
 *
 * @param { number | null } loginAt Timestamp (ms) when the session was established.
 * @param { number | null } sessionTtlHours Session TTL in hours from the server.
 * @returns { number | null } Returns ms until expiry, or null if data is unavailable.
 */
export const getMsUntilExpiry = (loginAt: number | null, sessionTtlHours: number | null): number | null => {
  if (loginAt === null || sessionTtlHours === null) {
    return null;
  }

  const expiresAt = loginAt + sessionTtlHours * 3_600_000;

  return expiresAt - Date.now();
};

/**
 * Custom hook that manages the full session lifecycle proactively.
 *
 * Three responsibilities:
 * 1. Load auth config (sessionTtlHours) once on mount.
 * 2. Send heartbeat keepalive every HEARTBEAT_INTERVAL_MS while authenticated.
 * 3. Proactively expire session via client-side clock every EXPIRY_CHECK_INTERVAL_MS.
 *
 * @returns { void } This hook returns nothing.
 */
export const useSessionLifecycle = (): void => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const sessionTtlHours = useAuthStore((s) => s.sessionTtlHours);
  const loadAuthConfig = useAuthStore((s) => s.loadAuthConfig);
  const keepAlive = useAuthStore((s) => s.keepAlive);
  const markSessionExpired = useAuthStore((s) => s.markSessionExpired);

  // #region Config load
  useEffect(() => {
    if (sessionTtlHours === null) {
      loadAuthConfig();
    }
  }, [sessionTtlHours, loadAuthConfig]);
  // #endregion

  // #region Heartbeat
  const sessionExpired = useAuthStore((s) => s.sessionExpired);

  useEffect(() => {
    if (!isAuthenticated || sessionExpired) {
      return;
    }

    const intervalId = setInterval(() => {
      keepAlive();
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, sessionExpired, keepAlive]);
  // #endregion

  // #region Expiry clock
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      const state = useAuthStore.getState();
      const ms = getMsUntilExpiry(state.loginAt, state.sessionTtlHours);

      if (ms !== null && ms <= 0 && !state.sessionExpired) {
        markSessionExpired();
      }
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, markSessionExpired]);
  // #endregion
};
